import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { randomString } from 'vcs-common';
import { toast } from 'react-toastify';

import dayjs from 'dayjs';

import { useNavigate } from 'react-router-dom';

import {
  Modal,
  Checkbox,
  CloseButton,
  Pressable,
  useFilePicker,
  Heading,
  Input,
  DatePickerCustom,
  TimePickerCustom,
  CustomSelect,
  Button,
  BackButton,
  Spinner,
} from '../../../shared/ui';
import { UploadAudioInnerIcon } from '../../icons/upload-audio-inner-icon';
import { PlayAudioIcon } from '../../icons/play-audio';
import { useAppSelector } from '../../../hooks/redux';

import { navigateToJournalSession } from '../../../pages/journal/journal-navigation';
import { startPaymentWidget } from '../../../utils/cloudPaumentUtils';
import { getMyTimeZone } from '../../../utils/getTime';
import {
  useCreateClientMutation,
  useCreateConsultationMutation,
  useGetClientsQuery,
} from '../../../services/users.api';
import {
  useCheckOfflineAudioAvailabilityMutation,
  useUpdateOfflineAudioLifecycleMutation,
} from '../../../services/consultations.api';
import {
  useCreateQuoteMutation,
  useGetUploadUrlMutation,
  useLazyGetTranscriptionByIdQuery,
  useStartTranscriptionMutation,
} from '../../../services/consultation-audio.api';
import { useCancelOrderMutation, useCreateTranscriptionCombinedOrderMutation } from '../../../services/billing.api';
import { usePosthogCapture } from '../../../hooks/usePosthogCapture';

const MEETING_FORMAT_OPTIONS = ['Оффлайн'] as const;

function consultationTypeFromFormatLabel(label: string): 'online' | 'offline' {
  return label === 'Онлайн' ? 'online' : 'offline';
}

function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} Мб`;
}

function addHourToTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + 60;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${nh.toString().padStart(2, '0')}:${nm.toString().padStart(2, '0')}`;
}

function isEndTimeAfterStart(start: string, end: string): boolean {
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  return endMinutes > startMinutes;
}

type UploadAudioModalProps = {
  isOpen: boolean;
  onClose: () => void;
  clientNames?: string[];
};

type PreparedFlow = {
  consultationId: string;
  clientId: number;
  recordingId: string;
  quoteToken: string;
  billableMinutes: number;
  ratePerMinute: number;
  estimatedCost: number;
  currency: string;
  publicOrderId: string;
  payableAmount: number;
  flowId: string;
};

type CreatedConsultationDraft = {
  consultationId: string;
  clientId: number;
  flowId: string;
};

function normalizeMessage(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getErrorMessage(error: unknown): string | null {
  const direct = normalizeMessage(error);
  if (direct) return direct;

  if (error instanceof Error) {
    const message = normalizeMessage(error.message);
    if (message) return message;
  }

  if (typeof error === 'object' && error !== null) {
    const maybeData = (error as { data?: unknown }).data;
    if (typeof maybeData === 'object' && maybeData !== null) {
      const data = maybeData as { message?: unknown; error?: unknown; detail?: unknown };
      return normalizeMessage(data.message) || normalizeMessage(data.error) || normalizeMessage(data.detail);
    }
  }

  return null;
}

const AUDIO_PAYMENT_PLAN_ID = 'audio_processing';

const AUDIO_FILE_ACCEPT = [
  'audio/*',
  '.aac',
  '.ac3',
  '.aif',
  '.aiff',
  '.alac',
  '.amr',
  '.caf',
  '.ec3',
  '.flac',
  '.m4a',
  '.m4b',
  '.mp2',
  '.mp3',
  '.mp4',
  '.oga',
  '.ogg',
  '.opus',
  '.wav',
  '.wave',
  '.webm',
  '.weba',
  '.wma',
  '.3gp',
  '.3gpp',
].join(',');

const AUDIO_MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  aac: 'audio/aac',
  ac3: 'audio/ac3',
  aif: 'audio/aiff',
  aiff: 'audio/aiff',
  alac: 'audio/alac',
  amr: 'audio/amr',
  caf: 'audio/x-caf',
  ec3: 'audio/eac3',
  flac: 'audio/flac',
  m4a: 'audio/mp4',
  m4b: 'audio/mp4',
  mp2: 'audio/mpeg',
  mp3: 'audio/mpeg',
  mp4: 'audio/mp4',
  oga: 'audio/ogg',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  wav: 'audio/wav',
  wave: 'audio/wav',
  weba: 'audio/webm',
  webm: 'audio/webm',
  wma: 'audio/x-ms-wma',
  '3gp': 'audio/3gpp',
  '3gpp': 'audio/3gpp',
};

const getAudioContentType = (file: File) => {
  if (file.type) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  return (extension && AUDIO_MIME_TYPES_BY_EXTENSION[extension]) || 'application/octet-stream';
};

export const UploadAudioModal: FC<UploadAudioModalProps> = ({ isOpen, onClose, clientNames = [] }) => {
  const userData = useAppSelector((state) => state.authSlice.userData);
  const navigate = useNavigate();
  const captureEvent = usePosthogCapture();
  const { data: clients } = useGetClientsQuery({});
  const [createClient] = useCreateClientMutation();
  const [createConsultation] = useCreateConsultationMutation();
  const [checkOfflineAudioAvailability] = useCheckOfflineAudioAvailabilityMutation();
  const [updateOfflineAudioLifecycle] = useUpdateOfflineAudioLifecycleMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();
  const [createQuote] = useCreateQuoteMutation();
  const [createTranscriptionCombinedOrder] = useCreateTranscriptionCombinedOrderMutation();
  const [cancelOrder] = useCancelOrderMutation();
  const [startTranscription] = useStartTranscriptionMutation();
  const [fetchTranscriptionStatus] = useLazyGetTranscriptionByIdQuery();

  const [clientName, setClientName] = useState('');
  const [meetingDate, setMeetingDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [meetingTime, setMeetingTime] = useState('12:00');
  const [meetingTimeEnd, setMeetingTimeEnd] = useState(addHourToTime('12:00'));
  const [meetingFormat, setMeetingFormat] = useState<string>(MEETING_FORMAT_OPTIONS[0]);
  const [file, setFile] = useState<File | null>(null);
  const [hasRecordingConsent, setHasRecordingConsent] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [uploadedDisplay, setUploadedDisplay] = useState<{ name: string; sizeMb: string } | null>(null);
  const [step, setStep] = useState<'form' | 'payment' | 'processing'>('form');
  const [isPreparing, setIsPreparing] = useState(false);
  const [isStartingTranscription, setIsStartingTranscription] = useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const [transcriptionStatusText, setTranscriptionStatusText] = useState<string | null>(null);
  const [preparedFlow, setPreparedFlow] = useState<PreparedFlow | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setFile(null);
      setHasRecordingConsent(false);
      setUploadedDisplay(null);
      setIsPreviewPlaying(false);
      setIsPreparing(false);
      setIsStartingTranscription(false);
      setIsPollingStatus(false);
      setIsPaymentConfirmed(false);
      setPreparedFlow(null);
      setTranscriptionStatusText(null);
      setMeetingTime('12:00');
      setMeetingTimeEnd(addHourToTime('12:00'));
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    } else {
      previewAudioRef.current?.pause();
      setFile(null);
      setHasRecordingConsent(false);
      setUploadedDisplay(null);
      setIsPreviewPlaying(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
      }
      previewAudioRef.current?.pause();
    };
  }, []);

  const isTimeRangeValid = isEndTimeAfterStart(meetingTime, meetingTimeEnd);
  const canSubmit = Boolean(clientName.trim() && file && hasRecordingConsent && isTimeRangeValid);

  const handleRemoveFile = () => {
    previewAudioRef.current?.pause();
    setFile(null);
    setUploadedDisplay(null);
    setIsPreviewPlaying(false);
  };

  const handleFiles = ([f]: File[]) => {
    setFile(f);
    setIsPreviewPlaying(false);
    setUploadedDisplay({
      name: f.name,
      sizeMb: formatFileSize(f.size),
    });
  };
  const audioPicker = useFilePicker({ accept: AUDIO_FILE_ACCEPT, onFiles: handleFiles });

  const handleTogglePreview = async () => {
    const audio = previewAudioRef.current;
    if (!audio || !previewUrl) return;

    if (audio.paused) {
      try {
        await audio.play();
      } catch (error) {
        console.error('offline_audio_preview_play_failed', error);
        setIsPreviewPlaying(false);
        toast.error('Не удалось воспроизвести выбранный аудиофайл');
      }
      return;
    }

    audio.pause();
  };

  const cancelPreparedFlow = async () => {
    if (!preparedFlow) return;

    await cancelOrder({ publicOrderId: preparedFlow.publicOrderId }).unwrap();
    await updateOfflineAudioLifecycle({
      id: Number(preparedFlow.consultationId),
      lifecycle_status: 'cancelled',
    }).unwrap();
  };

  const handleBack = () => {
    void (async () => {
      try {
        await cancelPreparedFlow();
        setPreparedFlow(null);
        setStep('form');
      } catch (error) {
        console.error('offline_audio_explicit_cancel_failed', { flowId: preparedFlow?.flowId, error });
        toast.error('Не удалось отменить черновик. Попробуйте закрыть окно ещё раз.');
      }
    })();
  };

  const openJournalEntry = useCallback(
    (consultationId: string, clientId: number) => {
      onClose();
      navigateToJournalSession(navigate, Number(consultationId), `/api/v1/clients/${clientId}`, 'Транскрипция');
    },
    [navigate, onClose],
  );

  const createOrFindClientAndConsultation = useCallback(async (): Promise<CreatedConsultationDraft | null> => {
    if (!userData?.id) return null;
    const name = clientName.trim();
    if (!name) return null;

    const consultationTz = await getMyTimeZone();
    const existingClient = clients?.find((c) => c.full_name === name);
    let clientId: number | undefined = existingClient?.id;

    if (!clientId) {
      const created = await createClient({
        full_name: name,
        time_zone: consultationTz,
        is_active: true,
        psychologists: [`/api/v1/users/${userData.id}`],
      }).unwrap();
      clientId = created.id;
    }

    const normalizedDate = `${dayjs(meetingDate).format('YYYY-MM-DD')}T00:00:00.000Z`;
    // randomString требует длину: без аргумента цикл не выполняется и вернётся ''.
    // Пустой code уезжал на бэкенд при создании черновика офлайн-аудио.
    const code = randomString(12);
    const flowId = randomString(12);

    const consultation = await createConsultation({
      client_entity: `/api/v1/clients/${clientId}`,
      date: normalizedDate,
      datetime_start: meetingTime,
      datetime_end: meetingTimeEnd,
      client_timezone: consultationTz,
      consultation_timezone: consultationTz,
      code,
      link: '',
      lifecycle_status: 'pending_payment',
      is_group: false,
      consultation_type: consultationTypeFromFormatLabel(meetingFormat),
    } as never).unwrap();

    return { consultationId: String(consultation.id), clientId, flowId };
  }, [
    clientName,
    clients,
    createClient,
    createConsultation,
    meetingDate,
    meetingFormat,
    meetingTime,
    meetingTimeEnd,
    userData?.id,
  ]);

  const pollTranscriptionStatus = useCallback(
    (transcriptionId: string) => {
      if (pollingTimerRef.current) {
        window.clearInterval(pollingTimerRef.current);
      }

      setIsPollingStatus(true);
      setTranscriptionStatusText('Транскрибация запущена. Ожидаем завершения...');

      const poll = async () => {
        try {
          const status = await fetchTranscriptionStatus(transcriptionId).unwrap();
          const normalizedStatus = (status.transcriptionStatus || status.status || '').toString().toLowerCase();

          if (normalizedStatus.includes('completed')) {
            setTranscriptionStatusText('Транскрибация завершена. Открываем запись...');
            setIsPollingStatus(false);
            captureEvent('offline_audio_transcription_completed');
            if (pollingTimerRef.current) {
              window.clearInterval(pollingTimerRef.current);
              pollingTimerRef.current = null;
            }
            if (preparedFlow?.consultationId && preparedFlow.clientId) {
              openJournalEntry(preparedFlow.consultationId, preparedFlow.clientId);
            }
            return;
          }

          if (
            normalizedStatus.includes('failed') ||
            normalizedStatus.includes('error') ||
            normalizedStatus.includes('cancel')
          ) {
            setTranscriptionStatusText('Транскрибация завершилась с ошибкой.');
            setIsPollingStatus(false);
            toast.error(getErrorMessage(status) ?? 'Транскрибация завершилась с ошибкой');
            if (pollingTimerRef.current) {
              window.clearInterval(pollingTimerRef.current);
              pollingTimerRef.current = null;
            }
          }
        } catch (error) {
          setTranscriptionStatusText('Не удалось получить статус транскрибации');
          console.error('offline_audio_transcription_poll_error', { transcriptionId });
          toast.error(getErrorMessage(error) ?? 'Не удалось получить статус транскрибации');
          setIsPollingStatus(false);
          if (pollingTimerRef.current) {
            window.clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
          }
        }
      };

      void poll();
      pollingTimerRef.current = window.setInterval(() => {
        void poll();
      }, 7000);
    },
    [captureEvent, fetchTranscriptionStatus, openJournalEntry, preparedFlow?.consultationId],
  );

  const handleSubmit = async () => {
    if (!canSubmit || !file) return;
    if (!isTimeRangeValid) {
      toast.error('Время окончания должно быть позже времени начала');
      return;
    }

    setIsPreparing(true);
    setTranscriptionStatusText(null);

    try {
      const consultationTz = await getMyTimeZone();
      await checkOfflineAudioAvailability({
        date: `${dayjs(meetingDate).format('YYYY-MM-DD')}T00:00:00.000Z`,
        datetime_start: meetingTime,
        datetime_end: meetingTimeEnd,
        consultation_timezone: consultationTz,
        consultation_type: consultationTypeFromFormatLabel(meetingFormat),
      } as never).unwrap();
      const contentType = getAudioContentType(file);
      const upload = await getUploadUrl({
        fileName: file.name,
        contentType,
        fileSize: file.size,
      }).unwrap();

      captureEvent('offline_audio_upload_started');
      const uploadResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('upload_failed');
      }

      const consultationDraft = await createOrFindClientAndConsultation();
      if (!consultationDraft) {
        throw new Error('consultation_not_created');
      }
      const { consultationId, clientId, flowId } = consultationDraft;

      const quote = await createQuote({
        recordingId: upload.recordingId,
        consultationId,
      }).unwrap();

      const order = await createTranscriptionCombinedOrder({
        quote_token: quote.quoteToken,
        consultation_id: consultationId,
        bonus_amount: 0,
      }).unwrap();

      setPreparedFlow({
        consultationId,
        clientId,
        recordingId: upload.recordingId,
        quoteToken: quote.quoteToken,
        billableMinutes: quote.billableMinutes ?? quote.durationMinutes ?? 0,
        ratePerMinute: quote.ratePerMinute,
        estimatedCost: quote.estimatedCost,
        currency: quote.currency,
        publicOrderId: order.public_order_id,
        payableAmount: order.payable_amount,
        flowId,
      });
      setStep('payment');
    } catch (error) {
      if (error instanceof Error && error.message === 'upload_failed') {
        toast.error('Не удалось загрузить файл в хранилище');
      } else {
        toast.error(getErrorMessage(error) ?? 'Не удалось подготовить оплату для транскрибации');
      }
    } finally {
      setIsPreparing(false);
    }
  };

  const startPaidTranscription = async () => {
    if (!preparedFlow) return;
    setStep('processing');
    setIsStartingTranscription(true);
    try {
      await updateOfflineAudioLifecycle({
        id: Number(preparedFlow.consultationId),
        lifecycle_status: 'processing',
      }).unwrap();
      const start = await startTranscription({
        publicOrderId: preparedFlow.publicOrderId,
        quoteToken: preparedFlow.quoteToken,
        consultationId: preparedFlow.consultationId,
        recordingId: preparedFlow.recordingId,
      }).unwrap();
      captureEvent('offline_audio_transcription_started');
      await updateOfflineAudioLifecycle({
        id: Number(preparedFlow.consultationId),
        lifecycle_status: 'ready',
      }).unwrap();
      setTranscriptionStatusText('Идёт ИИ-анализ записи');
      pollTranscriptionStatus(start.transcriptionId);
    } catch (error) {
      setStep('payment');
      setTranscriptionStatusText('Оплата прошла. Повторите запуск транскрибации — повторная оплата не потребуется.');
      toast.error(getErrorMessage(error) ?? 'Оплата прошла, но запустить транскрибацию не удалось');
    } finally {
      setIsStartingTranscription(false);
    }
  };

  const handlePay = () => {
    if (!userData?.email || !userData?.id || !preparedFlow || isStartingTranscription || isPollingStatus) return;
    if (isPaymentConfirmed) {
      void startPaidTranscription();
      return;
    }

    startPaymentWidget({
      planId: AUDIO_PAYMENT_PLAN_ID,
      userId: userData.id,
      amount: preparedFlow.payableAmount,
      currency: preparedFlow.currency,
      description: 'Оплата обработки аудиозаписи (транскрибация и ИИ-анализ)',
      email: userData.email,
      transactionId: preparedFlow.publicOrderId,
      bonusesUsed: 0,
      onSuccess: () => {
        captureEvent('offline_audio_payment_success', { consultation_id: preparedFlow.consultationId });
        setIsPaymentConfirmed(true);
        void startPaidTranscription();
      },
      onFail: () => {
        toast.info('Оплата не завершена. Вы можете повторить попытку.');
      },
    });
  };

  const handleClose = () => {
    if (preparedFlow && step === 'payment' && !isPaymentConfirmed) {
      void (async () => {
        try {
          await cancelPreparedFlow();
        } catch (error) {
          console.error('offline_audio_explicit_cancel_failed', { flowId: preparedFlow.flowId, error });
        } finally {
          onClose();
        }
      })();
      return;
    }
    onClose();
  };

  const dateDisplay = dayjs(meetingDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
  const paymentAmountText = useMemo(() => preparedFlow?.payableAmount ?? 0, [preparedFlow]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      // Clicking past the window closed it before; Escape did not, and the date picker uses it.
      closeOnBackdrop
      closeOnEscape={false}
      fullscreenOnMobile
      size={step === 'form' ? 'xl' : 'md'}
      title={
        step === 'payment' ? (
          <span className="flex items-center gap-2">
            {!isPaymentConfirmed && <BackButton onClick={handleBack} />}
            Аудиозапись загружена
          </span>
        ) : step === 'form' ? (
          'Загрузка аудиозаписи'
        ) : undefined
      }
    >
      <audio
        ref={previewAudioRef}
        src={previewUrl ?? undefined}
        className="hidden"
        preload="metadata"
        onPlay={() => setIsPreviewPlaying(true)}
        onPause={() => setIsPreviewPlaying(false)}
        onEnded={() => setIsPreviewPlaying(false)}
        onError={() => {
          setIsPreviewPlaying(false);
          toast.error('Не удалось декодировать выбранный аудиофайл');
        }}
      />

      {step === 'payment' ? (
        <>
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col gap-2 w-full">
              <div className="border border-line rounded-card p-4 flex flex-col gap-6 bg-white">
                <div className="flex flex-col gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="text-body-l-strong text-ink">Имя клиента</span>
                    <span className="min-w-0 break-words text-body-l text-ink">{clientName}</span>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="flex items-center gap-2">
                      <span className="text-body-l-strong text-ink">Дата</span>
                      <span className="text-body-l text-ink">{dateDisplay}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-body-l-strong text-ink">Время</span>
                      <span className="text-body-l text-ink">
                        {meetingTime} - {meetingTimeEnd}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="relative flex h-[52px] items-center rounded-popup bg-surface-muted px-4 pr-14 w-full">
                  <span className="text-body-l-strong text-ink flex-1 truncate mr-3">
                    {uploadedDisplay?.name} ({uploadedDisplay?.sizeMb})
                  </span>
                  <Pressable
                    className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-primary text-white hover:opacity-90 absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => {
                      void handleTogglePreview();
                    }}
                    disabled={!previewUrl}
                    aria-label={isPreviewPlaying ? 'Поставить запись на паузу' : 'Прослушать запись'}
                  >
                    {isPreviewPlaying ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                        <path d="M6 4v12M14 4v12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <PlayAudioIcon />
                    )}
                  </Pressable>
                </div>
              </div>
              <div className="flex flex-col gap-2 rounded-card border border-primary bg-surface-muted p-2 sm:flex-row sm:items-center sm:gap-3 sm:rounded-full sm:p-1">
                <div className="self-start rounded-full bg-ink-muted px-4 py-2 sm:self-auto">
                  <span className="text-display-s text-white">{preparedFlow?.ratePerMinute ?? 0} ₽/мин.</span>
                </div>
                <div className="flex min-w-0 flex-col gap-1 px-2 py-1 sm:flex-1 sm:flex-row sm:items-center sm:gap-4 sm:px-4 sm:py-2">
                  <span className="text-heading-lg text-ink sm:text-display-s">
                    {preparedFlow?.billableMinutes ?? 0} минут
                  </span>
                  <span className="break-words text-heading-lg font-normal text-ink sm:text-display-s">
                    {preparedFlow?.estimatedCost ?? 0} рублей
                  </span>
                </div>
              </div>
              {transcriptionStatusText && (
                <div className="rounded-control bg-surface-muted px-4 py-3">
                  <p className="text-ui text-ink">{transcriptionStatusText}</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-center">
            <Button
              size="lg"
              variant="primary"
              fullWidth
              className="sm:w-auto sm:px-10"
              onClick={handlePay}
              disabled={
                !userData?.email || !userData?.id || !preparedFlow || isStartingTranscription || isPollingStatus
              }
            >
              {isStartingTranscription
                ? 'Запускаем транскрибацию...'
                : isPaymentConfirmed
                  ? 'Повторить запуск транскрибации'
                  : `Оплатить ${paymentAmountText} рублей`}
            </Button>
          </div>
        </>
      ) : step === 'form' ? (
        <>
          <div className="flex min-w-0 flex-col gap-6">
            {uploadedDisplay ? (
              <div className="flex h-[52px] w-full max-w-full items-center gap-2 rounded-popup border border-line bg-surface-muted py-2 pl-4 pr-2 sm:w-fit">
                <span className="text-body-l-strong text-ink truncate">
                  {uploadedDisplay.name} ({uploadedDisplay.sizeMb})
                </span>
                <CloseButton label="Удалить файл" onClick={handleRemoveFile} />
              </div>
            ) : (
              <>
                {audioPicker.input}
                <Pressable
                  onClick={audioPicker.open}
                  className="relative flex h-[52px] w-full items-center gap-2 rounded-full border border-primary bg-surface-muted px-4 pr-12 text-left shadow-raised transition-colors sm:w-fit"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-ink">
                    <path d="M8 3.33v9.34M3.33 8h9.34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span className="mr-3 min-w-0 truncate text-body-l-strong text-ink">Загрузить аудиозапись</span>
                  <span className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shrink-0 absolute right-1 top-1/2 -translate-y-1/2">
                    <UploadAudioInnerIcon />
                  </span>
                </Pressable>
              </>
            )}

            <Checkbox
              size="sm"
              name="client-recording-consent"
              checked={hasRecordingConsent}
              onChange={(event) => setHasRecordingConsent(event.target.checked)}
              label="Подтверждаю, что получил(а) информированное согласие клиента на аудиозапись сессии."
            />

            <div className="w-full rounded-control bg-surface-muted px-4 py-2 sm:w-fit">
              <p className="text-heading-md text-ink leading-snug">
                Стоимость подготовки, транскрибации и ИИ-анализа аудиозаписи рассчитывается автоматически.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="sm:col-span-2">
                {clientNames.length > 0 ? (
                  <CustomSelect
                    size="lg"
                    label="Имя клиента"
                    required
                    helper="Введите имя или выберите из списка."
                    placeholder="Например, Максим"
                    items={clientNames}
                    selected={clientName}
                    onChange={setClientName}
                    showError={false}
                  />
                ) : (
                  <Input
                    size="lg"
                    label="Имя клиента"
                    required
                    hint="Введите имя или выберите из списка."
                    value={clientName}
                    placeholder="Например, Максим"
                    onChange={(e) => setClientName(e.target.value)}
                  />
                )}
              </div>
              <CustomSelect
                size="lg"
                label="Формат встречи"
                placeholder="Оффлайн"
                items={[...MEETING_FORMAT_OPTIONS]}
                selected={meetingFormat}
                onChange={setMeetingFormat}
                showError={false}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <DatePickerCustom size="lg" label="Дата встречи" required date={meetingDate} setDate={setMeetingDate} />
              <TimePickerCustom
                size="lg"
                label="Начало"
                value={meetingTime}
                onTimeChange={(value) => {
                  setMeetingTime(value);
                  if (!isEndTimeAfterStart(value, meetingTimeEnd)) {
                    setMeetingTimeEnd(addHourToTime(value));
                  }
                }}
              />
              <TimePickerCustom size="lg" label="Окончание" value={meetingTimeEnd} onTimeChange={setMeetingTimeEnd} />
            </div>
            {!isTimeRangeValid && (
              <p className="text-ui text-primary">Время окончания должно быть позже времени начала</p>
            )}

            <p className="text-ui text-ink leading-snug">
              *Все данные, включая заметки к сессиям и записи, шифруются во время передачи и хранения для обеспечения
              полной безопасности.
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              size="lg"
              variant="primary"
              fullWidth
              className="sm:w-auto sm:px-10"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={!canSubmit || isPreparing}
            >
              {isPreparing ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="sm" className="text-white" />
                  Анализируем аудио...
                </span>
              ) : (
                'Продолжить'
              )}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-6 py-8">
          <Spinner size="xl" className="text-primary" />
          <Heading size="lg" className="text-center">
            Идёт ИИ-анализ записи
          </Heading>
          <p className="text-body-l text-ink text-center max-w-[460px]">
            Это может занять несколько минут. После завершения транскрибации откроем вкладку «Транскрипция» в
            терапевтическом журнале.
          </p>
        </div>
      )}
    </Modal>
  );
};
